import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpGatewayService } from '../../../core/services/http-gateway.service';
import { UrlBuilder } from '../../../shared/utils/url-utils';
import { AccessRequest, AccessRequestStatus, CreateAccessRequestContract } from '../types/access-request';

const ACCESS_REQUEST_URLS = {
    BASE: 'api/v5/organization/{organizationId}/access-requests',
    MINE: 'api/v5/organization/{organizationId}/access-requests/mine',
    CANCEL: 'api/v5/organization/{organizationId}/access-requests/{requestId}/cancel',
    APPROVE: 'api/v5/organization/{organizationId}/access-requests/{requestId}/approve',
    REJECT: 'api/v5/organization/{organizationId}/access-requests/{requestId}/reject',
};

@Injectable({ providedIn: 'root' })
export class AccessRequestsService {
    private http = inject(HttpGatewayService);

    private readonly _pendingCount = signal(0);
    public readonly pendingCount = this._pendingCount.asReadonly();

    public create(organizationId: string, contract: CreateAccessRequestContract): Observable<AccessRequest[]> {
        const url = new UrlBuilder(ACCESS_REQUEST_URLS.BASE).addOrganizationId(organizationId).toUrl();

        return this.http.post<AccessRequest[]>(url, contract);
    }

    public getMine(organizationId: string): Observable<AccessRequest[]> {
        const url = new UrlBuilder(ACCESS_REQUEST_URLS.MINE).addOrganizationId(organizationId).toUrl();

        return this.http.get<AccessRequest[]>(url);
    }

    public getForOrganization(organizationId: string, status?: AccessRequestStatus): Observable<AccessRequest[]> {
        const builder = new UrlBuilder(ACCESS_REQUEST_URLS.BASE).addOrganizationId(organizationId);
        if (status) {
            builder.addQueryParameter('status', status);
        }

        return this.http.get<AccessRequest[]>(builder.toUrl());
    }

    public cancel(organizationId: string, requestId: string): Observable<AccessRequest> {
        return this.http.post<AccessRequest>(this.buildActionUrl(ACCESS_REQUEST_URLS.CANCEL, organizationId, requestId), {});
    }

    public approve(organizationId: string, requestId: string): Observable<AccessRequest> {
        return this.http.post<AccessRequest>(this.buildActionUrl(ACCESS_REQUEST_URLS.APPROVE, organizationId, requestId), {});
    }

    public reject(organizationId: string, requestId: string): Observable<AccessRequest> {
        return this.http.post<AccessRequest>(this.buildActionUrl(ACCESS_REQUEST_URLS.REJECT, organizationId, requestId), {});
    }

    public refreshPendingCount(organizationId: string): void {
        this.getForOrganization(organizationId, 'pending').subscribe({
            next: (requests) => this._pendingCount.set(requests.length),
            error: () => this._pendingCount.set(0),
        });
    }

    private buildActionUrl(template: string, organizationId: string, requestId: string): string {
        return new UrlBuilder(template).addOrganizationId(organizationId).addRequestId(requestId).toUrl();
    }
}
